'use client';

import React from 'react';
import Link from 'next/link';
import { Shield, ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} />
            Back to Ertzyx
          </Link>
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-blue-700" />
            <span className="font-bold text-slate-800">Ertzyx</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-slate-900 mb-3">Terms of Service</h1>
          <p className="text-slate-500 text-sm">
            Last updated: 25 March 2026 · Effective: 25 March 2026
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of the Ertzyx
            platform at{' '}
            <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
              ledger.ertzyx.com
            </a>{' '}
            (&quot;Platform&quot;), operated by Ertzyx (&quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;). By creating an account or using the Platform, you agree to these
            Terms. If you do not agree, do not use the Platform.
          </p>
        </div>

        <div className="space-y-10 text-slate-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Eligibility</h2>
            <p>
              You must be at least 16 years of age to use Ertzyx. By using the Platform, you
              represent that you meet this requirement and that all information you provide is
              accurate and complete. Institutions must be legally registered entities and must
              designate authorised representatives to manage their accounts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Account Registration</h2>
            <p className="mb-3">You are responsible for:</p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Maintaining the confidentiality of your login credentials</li>
              <li>All activity that occurs under your account</li>
              <li>
                Notifying us immediately of any unauthorised access at{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </li>
            </ul>
            <p className="mt-3">
              You may not share your account with others or create accounts on behalf of third
              parties without their explicit consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Acceptable Use Policy</h2>
            <p className="mb-3">
              You agree to use Ertzyx only for lawful purposes and in accordance with these Terms.
              You must not:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Submit false, fabricated, or misleading achievement records</li>
              <li>Impersonate any person, institution, or organisation</li>
              <li>Upload content that infringes third-party intellectual property rights</li>
              <li>Use the Platform to harass, threaten, or harm others</li>
              <li>
                Attempt to gain unauthorised access to any part of the Platform or its
                infrastructure
              </li>
              <li>
                Use automated tools, bots, or scrapers to access the Platform without our written
                consent
              </li>
              <li>Interfere with or disrupt the integrity or performance of the Platform</li>
              <li>Use the Platform for any illegal purpose under applicable law</li>
              <li>Circumvent any security or access control measures</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              4. No False Achievement Submissions
            </h2>
            <p className="mb-3">
              The integrity of the Ertzyx ledger depends on the truthfulness of all submitted
              records. You expressly agree that:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>All achievements you submit are genuine and accurately described</li>
              <li>You will not fabricate, exaggerate, or misrepresent any achievement</li>
              <li>
                You will not submit achievements on behalf of another person without their explicit
                written consent
              </li>
              <li>
                You understand that submitting false records may constitute fraud and may result in
                immediate account termination and referral to relevant authorities
              </li>
            </ul>
            <p className="mt-3">
              We reserve the right to investigate and remove any records we reasonably believe to be
              false or misleading.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              5. Institution Verification Responsibilities
            </h2>
            <p className="mb-3">
              Institutions that partner with Ertzyx to verify achievement records accept the
              following responsibilities:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Only authorised representatives of the institution may issue verifications</li>
              <li>
                Institutions must verify only records that fall within their legitimate authority
                and expertise
              </li>
              <li>Institutions must not verify records they know or suspect to be false</li>
              <li>
                Institutions are responsible for maintaining the accuracy of their institutional
                profile and contact information
              </li>
              <li>
                Institutions must promptly notify Ertzyx if their account credentials are
                compromised
              </li>
              <li>
                Institutions acknowledge that their verification actions create a permanent,
                auditable record on the platform
              </li>
            </ul>
            <p className="mt-3">
              Ertzyx reserves the right to suspend or terminate institutional accounts that violate
              these responsibilities or that issue verifications in bad faith.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              6. Intellectual Property of Uploaded Content
            </h2>
            <p className="mb-3">
              You retain all intellectual property rights in the content you upload to Ertzyx,
              including documents, images, and media files. By uploading content, you grant Ertzyx a
              limited, non-exclusive, royalty-free licence to:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Store and display your content as part of the Platform&apos;s services</li>
              <li>Generate public credential pages when you choose to make records public</li>
              <li>Process and transmit your content to facilitate verification requests</li>
            </ul>
            <p className="mt-3">
              This licence does not grant us the right to sell, sublicense, or use your content for
              advertising. You represent that you own or have the necessary rights to all content
              you upload, and that it does not infringe any third-party rights.
            </p>
            <p className="mt-3">
              Ertzyx&apos;s own intellectual property — including the platform design, software,
              trademarks, and branding — remains our exclusive property. You may not copy,
              reproduce, or create derivative works from our platform without written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              7. Account Termination Policy
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Termination by You</h3>
                <p className="text-slate-600">
                  You may delete your account at any time via Settings → Account → Delete Account.
                  Upon deletion, your personal data will be removed in accordance with our{' '}
                  <Link href="/data-retention" className="text-blue-700 underline">
                    Data Retention Policy
                  </Link>
                  . Verified credential records that have been made public may remain accessible to
                  holders of the credential link for a transition period.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Termination by Ertzyx</h3>
                <p className="text-slate-600">
                  We may suspend or terminate your account immediately, without prior notice, if we
                  determine that you have:
                </p>
                <ul className="mt-2 space-y-1 list-disc list-inside text-slate-500 text-sm">
                  <li>Violated these Terms of Service</li>
                  <li>Submitted false or fraudulent records</li>
                  <li>Engaged in conduct harmful to other users or the platform</li>
                  <li>Failed to pay applicable fees after notice</li>
                  <li>Violated applicable law</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-2">Effect of Termination</h3>
                <p className="text-slate-600">
                  Upon termination, your right to access the Platform ceases immediately. Provisions
                  of these Terms that by their nature should survive termination (including
                  intellectual property, limitation of liability, and dispute resolution) will
                  survive.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              8. Payments and Subscriptions
            </h2>
            <p>
              Certain features of Ertzyx may require payment. Payments are processed by a
              third-party payment processing provider. By providing payment information, you
              authorise us to charge the applicable fees. Fees are non-refundable except as
              required by law or as explicitly stated in our refund policy. We reserve the right to
              change pricing with 30 days&apos; notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Disclaimer of Warranties</h2>
            <p>
              The Platform is provided &quot;as is&quot; and &quot;as available&quot; without
              warranties of any kind, either express or implied. We do not warrant that the Platform
              will be uninterrupted, error-free, or completely secure. We do not warrant the
              accuracy or completeness of any content on the Platform, including user-submitted
              achievement records.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
            <p className="mb-3">
              To the fullest extent permitted by applicable law, Ertzyx and its officers, directors,
              employees, and agents shall not be liable for:
            </p>
            <ul className="space-y-2 list-disc list-inside text-slate-600">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, data, goodwill, or business opportunities</li>
              <li>
                Damages arising from reliance on user-submitted content or verification records
              </li>
              <li>Damages arising from unauthorised access to your account</li>
              <li>
                Any damages exceeding the amount you paid to Ertzyx in the 12 months preceding the
                claim
              </li>
            </ul>
            <p className="mt-3 text-sm text-slate-500">
              Some jurisdictions do not allow the exclusion of certain warranties or limitation of
              liability. In such jurisdictions, our liability is limited to the maximum extent
              permitted by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Ertzyx and its affiliates from any claims,
              damages, losses, or expenses (including legal fees) arising from your use of the
              Platform, your violation of these Terms, or your infringement of any third-party
              rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              12. Governing Law and Disputes
            </h2>
            <p>
              These Terms are governed by applicable law. Any disputes arising from these Terms or
              your use of the Platform shall first be attempted to be resolved through good-faith
              negotiation. If unresolved, disputes may be submitted to binding arbitration or the
              courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes
              via email or a prominent notice on the Platform at least 14 days before the changes
              take effect. Continued use of the Platform after changes constitutes acceptance of the
              updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact</h2>
            <p>For questions about these Terms:</p>
            <div className="mt-3 bg-white border border-slate-200 rounded-xl p-5">
              <p className="font-semibold text-slate-800">Ertzyx Legal</p>
              <p className="text-slate-600 text-sm mt-1">
                Email:{' '}
                <a href="mailto:support@ertzyx.com" className="text-blue-700 underline">
                  support@ertzyx.com
                </a>
              </p>
              <p className="text-slate-600 text-sm">
                Platform:{' '}
                <a href="https://ledger.ertzyx.com" className="text-blue-700 underline">
                  ledger.ertzyx.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer nav */}
      <div className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap gap-4 text-sm text-slate-500">
          <Link href="/privacy-policy" className="hover:text-slate-800 transition-colors">
            Privacy Policy
          </Link>
          <Link href="/data-retention" className="hover:text-slate-800 transition-colors">
            Data Retention
          </Link>
          <Link href="/cookie-policy" className="hover:text-slate-800 transition-colors">
            Cookie Policy
          </Link>
          <Link href="/contact" className="hover:text-slate-800 transition-colors">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
